(function () {
  "use strict";

  function setAnswerState(answerBox, state, text) {
    answerBox.hidden = false;
    answerBox.setAttribute("data-state", state);
    answerBox.textContent = text;
  }

  // Only ever render sourceUrl as a clickable link if it's actually on our
  // own domain — the AI-generated value is never trusted blindly.
  var STORE_ORIGIN_PATTERN = /^https:\/\/(www\.)?cuttingedgeknives\.co\.uk\//;

  // Renders a successful answer as its own paragraph, plus (if the AI named a
  // source page) a second paragraph linking to it. Built with DOM methods
  // rather than innerHTML since reply/sourceLabel are AI-generated text and
  // must never be parsed as markup.
  function renderAnswer(answerBox, data) {
    answerBox.hidden = false;
    answerBox.setAttribute("data-state", "done");
    answerBox.textContent = "";

    var replyPara = document.createElement("p");
    replyPara.textContent = data.reply;
    answerBox.appendChild(replyPara);

    if (data.sourceUrl && data.sourceLabel && STORE_ORIGIN_PATTERN.test(data.sourceUrl)) {
      var linkPara = document.createElement("p");
      var link = document.createElement("a");
      link.href = data.sourceUrl;
      link.textContent = data.answered ? "View the full " + data.sourceLabel + " page" : data.sourceLabel;
      linkPara.appendChild(link);
      answerBox.appendChild(linkPara);
    }
  }

  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function isFieldValid(field) {
    var value = (field.value || "").trim();
    if (!value) return false;
    if (field.type === "email") return EMAIL_PATTERN.test(value);
    return true;
  }

  // Validates every [required] field in the form, marking invalid ones with
  // aria-invalid (styled with a red border in ai-contact-form.css) instead of
  // relying on the browser's native validation bubbles (the form has
  // novalidate so we control the error presentation ourselves).
  function validateForm(form) {
    var firstInvalid = null;

    form.querySelectorAll("[required]").forEach(function (field) {
      if (isFieldValid(field)) {
        field.removeAttribute("aria-invalid");
      } else {
        field.setAttribute("aria-invalid", "true");
        if (!firstInvalid) firstInvalid = field;
      }
    });

    return firstInvalid;
  }

  function initAsk(root, workerUrl) {
    var askInput = root.querySelector("[data-ai-ask-input]");
    var askButton = root.querySelector("[data-ai-ask-submit]");
    var answerBox = root.querySelector("[data-ai-answer]");
    if (!askInput || !askButton || !answerBox) return;

    var history = [];

    function askQuestion() {
      var question = (askInput.value || "").trim();
      if (!question) return;

      askButton.disabled = true;
      askInput.disabled = true;
      setAnswerState(answerBox, "loading", "Thinking…");

      history.push({ role: "user", content: question });

      fetch(workerUrl + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: history.slice(-8) }),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              throw new Error(data && data.error ? data.error : "Request failed.");
            }
            return data;
          });
        })
        .then(function (data) {
          var reply = data.reply || "Sorry, something went wrong - please use the form below.";
          history.push({ role: "assistant", content: reply });
          renderAnswer(answerBox, {
            reply: reply,
            answered: data.answered,
            sourceUrl: data.sourceUrl,
            sourceLabel: data.sourceLabel,
          });
        })
        .catch(function () {
          setAnswerState(
            answerBox,
            "error",
            "Sorry, something went wrong - please use the form below and we'll get back to you.",
          );
        })
        .finally(function () {
          askButton.disabled = false;
          askInput.disabled = false;
        });
    }

    askButton.addEventListener("click", askQuestion);
    askInput.addEventListener("keydown", function (event) {
      if (event.key === "Enter") {
        event.preventDefault();
        askQuestion();
      }
    });
  }

  function initTurnstile(root, siteKey) {
    var container = root.querySelector("[data-ai-turnstile]");
    if (!container || !siteKey) return;

    root._turnstileContainer = container;

    function render() {
      if (window.turnstile && !root._turnstileWidgetId) {
        root._turnstileWidgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          callback: function () {
            // Verified — the widget has nothing left to tell the customer.
            container.hidden = true;
          },
        });
      }
    }

    if (window.turnstile) {
      render();
      return;
    }

    // The Turnstile script tag loads asynchronously — poll briefly until it's ready.
    var attempts = 0;
    var interval = setInterval(function () {
      attempts += 1;
      if (window.turnstile) {
        render();
        clearInterval(interval);
      } else if (attempts > 40) {
        clearInterval(interval); // ~10s — give up quietly, form still works, just unverified
      }
    }, 250);
  }

  function initForm(root, workerUrl) {
    var form = root.querySelector("[data-ai-contact-submit]");
    var statusBox = root.querySelector("[data-ai-form-status]");
    var submitButton = root.querySelector("[data-ai-submit-button]");
    if (!form || !statusBox || !submitButton) return;

    form.addEventListener("input", function (event) {
      if (event.target.hasAttribute("required") && isFieldValid(event.target)) {
        event.target.removeAttribute("aria-invalid");
      }
    });

    form.addEventListener("submit", function (event) {
      event.preventDefault();

      var firstInvalid = validateForm(form);
      if (firstInvalid) {
        statusBox.hidden = false;
        statusBox.setAttribute("data-state", "error");
        statusBox.textContent = "Please fill in all highlighted required fields.";
        firstInvalid.focus();
        return;
      }

      var formData = new FormData(form);
      submitButton.disabled = true;
      statusBox.hidden = true;

      fetch(workerUrl + "/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.get("name"),
          email: formData.get("email"),
          orderNumber: formData.get("order_number"),
          subject: formData.get("subject"),
          message: formData.get("message"),
          honeypot: formData.get("hp_note"),
          turnstileToken: formData.get("cf-turnstile-response"),
        }),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok || !data.success) {
              throw new Error(data && data.error ? data.error : "Something went wrong.");
            }
            return data;
          });
        })
        .then(function () {
          statusBox.hidden = false;
          statusBox.setAttribute("data-state", "success");
          statusBox.textContent = "Thanks - your message has been sent. We'll get back to you as soon as possible.";
          form.reset();
          if (window.turnstile && root._turnstileWidgetId) {
            window.turnstile.reset(root._turnstileWidgetId);
            if (root._turnstileContainer) root._turnstileContainer.hidden = false;
          }
        })
        .catch(function (err) {
          statusBox.hidden = false;
          statusBox.setAttribute("data-state", "error");
          statusBox.textContent =
            (err && err.message) ||
            "Something went wrong - please email us directly at hello@cuttingedgeknives.co.uk.";
        })
        .finally(function () {
          submitButton.disabled = false;
          // hover-button (see theme.js) sets aria-busy on native form submit and
          // only clears it on `pageshow` — which never fires here since this
          // form submits via fetch(), not a real navigation. Clear it ourselves.
          submitButton.removeAttribute("aria-busy");
        });
    });
  }

  function init(root) {
    var workerUrl = (root.getAttribute("data-worker-url") || "").replace(/\/$/, "");
    if (!workerUrl) {
      console.warn("AI contact form: no backend URL configured on this block — set it in the theme editor.");
      return;
    }

    var siteKey = root.getAttribute("data-turnstile-site-key") || "";

    initAsk(root, workerUrl);
    initTurnstile(root, siteKey);
    initForm(root, workerUrl);
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-ai-contact-form]").forEach(init);
  });
})();
